import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { api, type TriggerRequest, newId } from "../api";

/**
 * Starting a run by date range, or by one Sentry issue.
 *
 * The two modes are exclusive in the form because they are exclusive in the
 * API — they use different Sentry endpoints, and sending both would silently
 * ignore one rather than erroring.
 *
 * There is no budget field, no diff ceiling and no protected paths. Those are
 * project configuration, where changing them is deliberate; a trigger is
 * something you click in a hurry.
 */
export function Trigger({ projectId, serviceName }: { projectId: string; serviceName: string }) {
  const [mode, setMode] = useState<"window" | "issue">("window");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [maxIssues, setMaxIssues] = useState(3);
  const [issueId, setIssueId] = useState("");
  const [live, setLive] = useState(false);

  const queries = useQueryClient();
  const navigate = useNavigate();

  const run = useMutation({
    mutationFn: () => {
      const body: TriggerRequest = {
        project_id: projectId,
        service_name: serviceName,
        dry_run: !live,
        // A key per submission, so a double-click returns the first run rather
        // than starting a second.
        idempotency_key: newId(),
      };
      if (mode === "issue") {
        body.sentry_issue_id = issueId.trim();
      } else {
        body.start = new Date(start).toISOString();
        body.end = new Date(end).toISOString();
        body.max_issues = maxIssues;
      }
      return api.trigger(body);
    },
    onSuccess: (result) => {
      queries.invalidateQueries({ queryKey: ["runs"] });
      navigate(`/runs/${result.run_id}`);
    },
  });

  return (
    <form
      className="trigger"
      onSubmit={(event) => {
        event.preventDefault();
        run.mutate();
      }}
    >
      <h2>Start a run</h2>

      <div className="modes">
        <label>
          <input
            type="radio"
            checked={mode === "window"}
            onChange={() => setMode("window")}
          />
          Date range
        </label>
        <label>
          <input type="radio" checked={mode === "issue"} onChange={() => setMode("issue")} />
          One Sentry issue
        </label>
      </div>

      {mode === "window" ? (
        <div className="fields">
          <label>
            From (UTC)
            <input
              type="datetime-local"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              required
            />
          </label>
          <label>
            To (UTC)
            <input
              type="datetime-local"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              required
            />
          </label>
          <label>
            Issues to pick
            <input
              type="number"
              min={1}
              max={50}
              value={maxIssues}
              onChange={(e) => setMaxIssues(Number(e.target.value))}
            />
          </label>
        </div>
      ) : (
        <div className="fields">
          <label>
            Sentry issue ID
            <input
              value={issueId}
              onChange={(e) => setIssueId(e.target.value)}
              placeholder="4507118293"
              required
            />
          </label>
        </div>
      )}

      <label className="live">
        <input type="checkbox" checked={live} onChange={(e) => setLive(e.target.checked)} />
        Live run — the agent may act on what it finds
      </label>
      {/* Stated rather than implied: what actually happens on a live run is
          governed by the project's write mode, not by this checkbox. */}
      <p className="muted">
        A dry run scores and records issues without dispatching any work. A live
        run dispatches, and what it may do to the repository is the project's
        write mode.
      </p>

      <button type="submit" disabled={run.isPending}>
        {run.isPending ? "Starting…" : live ? "Start live run" : "Start dry run"}
      </button>

      {run.error ? <p className="error">{run.error.message}</p> : null}
      {/* Clamping is reported, never silent: honouring 3 when the operator
          typed 10 without saying so reads as "it ignored me". */}
      {run.data?.clamped_by ? (
        <p className="warn">
          Asked for {run.data.requested_n}, ran {run.data.effective_n} — limited by{" "}
          <code>{run.data.clamped_by}</code>.
        </p>
      ) : null}
    </form>
  );
}
